import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '../../lib/utils';
import { EASE_OUT } from '../../lib/motion';

/*
 * Divider drawn like a circuit trace (the DVS monogram motif): a line with a 45° jog that
 * ends in a solder pad, drawing itself in when scrolled into view. `jog` is where the bend
 * sits as a fraction of the width. The SVG is measured and drawn in real pixels: stretching
 * a viewBox (and non-scaling strokes) breaks the pathLength draw-in in Chromium.
 */
const TraceRule = ({ className, jog = 0.18, delay = 0 }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const a = Math.round(jog * width);
  const end = Math.max(a + 12, width - 8);
  const d = `M0 1.5 H${a} L${a + 7} 8.5 H${end - 4}`;
  const inView = { once: true, amount: 0.6 };

  return (
    <svg ref={ref} height="12" className={cn('block w-full overflow-visible text-border', className)} aria-hidden="true">
      {width > 0 && (
        <>
          <motion.path
            d={d}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            initial={reduced ? false : { pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={inView}
            transition={{ duration: 1.4, ease: EASE_OUT, delay }}
          />
          <motion.circle
            cx={end}
            cy="8.5"
            r="3.5"
            fill="none"
            stroke="hsl(var(--brand))"
            strokeWidth="1"
            initial={reduced ? false : { opacity: 0, scale: 0.4 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={inView}
            transition={{ delay: delay + 0.9, duration: 0.5, ease: EASE_OUT }}
          />
        </>
      )}
    </svg>
  );
};

export default TraceRule;
